import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  BarChart3,
  Copy,
  Crown,
  Hash,
  ImagePlus,
  Link2,
  Save,
  Shield,
  Trash2,
  UserMinus,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button, Input, Select, Textarea } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { cn } from '@/lib/utils';
import { toast } from '@/components/feedback/toast';
import type { Community } from '@/types';
import type { ViewMember, ViewMessage } from '../api/communityView';
import { useUpdateCommunity } from '../hooks/useCommunities';
import { uploadCommunityBanner } from '../api/banners';
import type { Channel } from './channels';
import { avatarBg, avatarInitial, displayName } from './cvHelpers';

export type AdminTab = 'general' | 'channels' | 'roles' | 'members' | 'invites' | 'moderation' | 'analytics' | 'danger';

interface AdminSettingsDrawerProps {
  open: boolean;
  initialTab: AdminTab;
  community: Community;
  channels: Channel[];
  members: ViewMember[];
  messages: ViewMessage[];
  onClose: () => void;
  onTabChange: (tab: AdminTab) => void;
  onOpenChannelEditor: (channel?: Channel) => void;
  onDeleteChannel: (id: string) => void;
}

const TABS: { id: AdminTab; label: string; icon: LucideIcon }[] = [
  { id: 'general', label: 'General', icon: Shield },
  { id: 'channels', label: 'Channels', icon: Hash },
  { id: 'roles', label: 'Roles & permissions', icon: Crown },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'invites', label: 'Invites', icon: Link2 },
  { id: 'moderation', label: 'Moderation', icon: Shield },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'danger', label: 'Danger zone', icon: Trash2 },
];

const FIELD = 'border-edge bg-black/25 focus:bg-black/40';

export function AdminSettingsDrawer({
  open,
  initialTab,
  community,
  channels,
  members,
  messages,
  onClose,
  onTabChange,
  onOpenChannelEditor,
  onDeleteChannel,
}: AdminSettingsDrawerProps) {
  const { user } = useAuth();
  const updateCommunity = useUpdateCommunity(community.id);
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const [memberSearch, setMemberSearch] = useState('');
  const [requireApproval, setRequireApproval] = useLocalSetting(`${community.id}:approval`, false);
  const [generalName, setGeneralName] = useState(community.name);
  const [generalDesc, setGeneralDesc] = useState(community.description ?? community.tagline ?? '');
  const [generalAccess, setGeneralAccess] = useState(accessValue(community));
  const [banner, setBanner] = useState(community.banner ?? '');

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [initialTab, open]);

  useEffect(() => {
    if (!open) return;
    setGeneralName(community.name);
    setGeneralDesc(community.description ?? community.tagline ?? '');
    setGeneralAccess(accessValue(community));
    setBanner(community.banner ?? '');
  }, [community, open]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const name = displayName(m.profile).toLowerCase();
      const handle = (m.profile?.handle ?? '').toLowerCase();
      return name.includes(q) || handle.includes(q) || m.user_id.toLowerCase().includes(q);
    });
  }, [memberSearch, members]);

  if (!open) return null;

  const setActive = (next: AdminTab) => {
    setTab(next);
    onTabChange(next);
  };

  const inviteUrl = `${window.location.origin}/c/${community.id}`;
  const reports = readJson<unknown[]>('winr-reports', []);
  const blocked = readJson<unknown[]>('winr-blocked-users', []);
  const paid = generalAccess === 'paid';
  const hasDirtyGeneral =
    generalName.trim() !== community.name ||
    generalDesc.trim() !== (community.description ?? community.tagline ?? '') ||
    generalAccess !== accessValue(community) ||
    (banner.trim() || null) !== (community.banner ?? null);

  function onBanner(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    // Optimized upload to storage — data URLs in `communities.banner` are banned.
    void uploadCommunityBanner(file, user.id)
      .then(setBanner)
      .catch((err: Error) => toast(err.message || 'Banner upload failed', 'error'));
  }

  async function saveGeneral() {
    const name = generalName.trim();
    if (name.length < 2) {
      toast('Community name must be at least 2 characters', 'error');
      return;
    }
    try {
      await updateCommunity.mutateAsync({
        name,
        description: generalDesc.trim() || null,
        tagline: generalDesc.trim() || null,
        access: paid ? 'paid' : 'free',
        privacy: generalAccess === 'private' ? 'private' : 'public',
        price_cents: paid ? Math.max(community.price_cents, 100) : 0,
        banner: banner.trim() || null,
      });
      toast('Community settings saved', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not save community settings', 'error');
    }
  }

  return (
    <div className="fixed inset-0 z-[300]">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-md" onClick={onClose} />
      <aside className="absolute bottom-0 right-0 top-0 grid w-[min(960px,95vw)] grid-cols-[240px_1fr] overflow-hidden border-l border-white/10 bg-[rgba(10,12,16,0.92)] shadow-[-20px_0_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl max-[900px]:grid-cols-1">
        <nav className="flex min-h-0 flex-col overflow-y-auto border-r border-edge p-2.5 max-[900px]:hidden">
          <div className="px-3 pb-3 pt-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.7px] text-lo">Settings</div>
            <div className="mt-1 truncate text-sm font-extrabold text-hi">{community.name}</div>
          </div>
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                className={cn(
                  'flex h-9 items-center gap-2.5 rounded-lg px-3 text-left text-[13px] font-semibold transition-colors',
                  id === 'danger'
                    ? 'text-danger hover:bg-danger/[0.08]'
                    : active
                      ? 'bg-white/[0.08] text-hi'
                      : 'text-mid hover:bg-white/[0.05] hover:text-hi',
                )}
                onClick={() => setActive(id)}
              >
                <Icon size={15} />
                {label}
              </button>
            );
          })}
        </nav>

        <main className="flex min-h-0 flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-edge px-6 py-5">
            <div>
              <h2 className="text-xl font-black tracking-[-0.4px] text-hi">{labelFor(tab)}</h2>
              <p className="mt-0.5 text-sm text-mid">{subFor(tab)}</p>
            </div>
            <button
              type="button"
              className="grid h-8 w-8 place-items-center rounded-lg text-mid transition-colors hover:bg-white/[0.06] hover:text-hi"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {tab === 'general' && (
              <Section>
                <Field label="Community name">
                  <Input value={generalName} onChange={(e) => setGeneralName(e.target.value)} className={FIELD} />
                </Field>
                <Field label="Description">
                  <Textarea value={generalDesc} onChange={(e) => setGeneralDesc(e.target.value)} className={FIELD} rows={4} />
                </Field>
                <Field label="Access">
                  <Select value={generalAccess} onChange={(e) => setGeneralAccess(e.target.value)} className={FIELD}>
                    <option value="free">Free public</option>
                    <option value="paid">Paid public</option>
                    <option value="private">Private invite-only</option>
                  </Select>
                </Field>
                <Field label="Live banner">
                  <div className="overflow-hidden rounded-xl border border-edge bg-white/[0.025]">
                    <div
                      className="relative h-36 bg-cover bg-center"
                      style={
                        banner.trim()
                          ? { backgroundImage: `url(${banner.trim()})` }
                          : { background: `linear-gradient(135deg, ${community.color}66, rgba(255,255,255,0.04))` }
                      }
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(8,9,12,0.78))]" />
                      <div className="absolute bottom-3 left-3 text-sm font-black text-hi">{generalName.trim() || community.name}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 p-3">
                      <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-pill bg-white px-4 text-[13px] font-semibold text-black transition-all hover:brightness-105">
                        <input type="file" accept="image/*" className="sr-only" onChange={onBanner} />
                        <ImagePlus size={15} /> Upload banner
                      </label>
                      <Button variant="outline" size="sm" onClick={() => setBanner('')}>
                        Remove
                      </Button>
                      <Input
                        placeholder="Or paste an image URL..."
                        value={banner}
                        onChange={(e) => setBanner(e.target.value)}
                        className={cn(FIELD, 'min-w-[240px] flex-1')}
                      />
                    </div>
                  </div>
                </Field>
                <ToggleRow
                  title="Require approval"
                  desc="New joiners must be approved by a mod before posting."
                  checked={requireApproval}
                  onToggle={() => setRequireApproval(!requireApproval)}
                />
                <Button className="bg-white text-black hover:brightness-105" onClick={() => void saveGeneral()} disabled={!hasDirtyGeneral || updateCommunity.isPending}>
                  <Save size={15} />
                  {updateCommunity.isPending ? 'Saving...' : 'Save live changes'}
                </Button>
              </Section>
            )}

            {tab === 'channels' && (
              <Section>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="text-sm text-mid">{channels.length} channel{channels.length === 1 ? '' : 's'} total</div>
                  <Button className="bg-white text-black hover:brightness-105" onClick={() => onOpenChannelEditor()}>
                    New channel
                  </Button>
                </div>
                {channels.map((ch) => (
                  <RowCard key={ch.id} icon={<Hash size={16} />}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-hi">#{ch.name}</div>
                      <div className="mt-0.5 text-xs text-lo">
                        {ch.section} - {ch.kind}
                        {ch.readOnly ? ' - read-only' : ''}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => onOpenChannelEditor(ch)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-danger hover:bg-danger/[0.08] hover:text-danger" onClick={() => onDeleteChannel(ch.id)}>
                      Delete
                    </Button>
                  </RowCard>
                ))}
              </Section>
            )}

            {tab === 'roles' && (
              <Section>
                {[
                  ['Owner', 'Full control over billing, settings, roles, members, and channels.'],
                  ['Admin', 'Manage channels, members, messages, moderation, invites, and analytics.'],
                  ['Member', 'Read and post in allowed channels.'],
                ].map(([name, desc]) => (
                  <RowCard key={name} icon={<Crown size={16} />}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-hi">{name}</div>
                      <div className="mt-0.5 text-xs text-lo">{desc}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => toast('Granular role editing coming soon', 'info')}>
                      Configure
                    </Button>
                  </RowCard>
                ))}
              </Section>
            )}

            {tab === 'members' && (
              <Section>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="text-sm text-mid">{members.length} member{members.length === 1 ? '' : 's'}</div>
                  <Input placeholder="Search members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} className={cn(FIELD, 'max-w-xs')} />
                </div>
                {filteredMembers.map((m) => {
                  const name = displayName(m.profile);
                  return (
                    <RowCard key={m.user_id} icon={<AvatarCell avatarUrl={m.profile?.avatar_url} name={name} />}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-hi">
                          {name} {m.role === 'OWNER' && <span className="ml-1 rounded-pill bg-amber-400/15 px-2 py-0.5 text-[10px] font-black text-amber-300">OWNER</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-lo">
                          {m.profile?.handle ?? m.user_id} - {m.role}
                        </div>
                      </div>
                      {m.role !== 'OWNER' && (
                        <Button variant="ghost" size="sm" className="text-danger hover:bg-danger/[0.08] hover:text-danger" onClick={() => toast('Member removal requires backend role enforcement', 'info')}>
                          <UserMinus size={14} /> Remove
                        </Button>
                      )}
                    </RowCard>
                  );
                })}
              </Section>
            )}

            {tab === 'invites' && (
              <Section>
                <Field label="Permanent invite link">
                  <div className="flex gap-2">
                    <Input value={inviteUrl} readOnly className={FIELD} onClick={(e) => e.currentTarget.select()} />
                    <Button className="bg-white text-black hover:brightness-105" onClick={() => void navigator.clipboard?.writeText(inviteUrl).then(() => toast('Invite link copied', 'ok'))}>
                      <Copy size={15} /> Copy
                    </Button>
                  </div>
                </Field>
                <ToggleRow title="Require approval" desc="New joiners must be approved before posting." checked={requireApproval} onToggle={() => setRequireApproval(!requireApproval)} />
                <RowCard icon={<Link2 size={16} />}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-hi">Expiring links</div>
                    <div className="mt-0.5 text-xs text-lo">Generate temporary invite links that expire after 7 days.</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => toast('Expiring links coming soon', 'info')}>Generate</Button>
                </RowCard>
              </Section>
            )}

            {tab === 'moderation' && (
              <Section>
                <Stats items={[['Banned users', blocked.length], ['Open reports', reports.length], ['Warnings', 0]]} />
                <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-[0.7px] text-lo">Recent reports</div>
                {reports.length ? (
                  reports.slice(0, 10).map((_, i) => (
                    <RowCard key={i} icon={<Shield size={16} />}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-hi">Report #{i + 1}</div>
                        <div className="mt-0.5 text-xs text-lo">Needs review</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => toast('Report resolution coming soon', 'info')}>Resolve</Button>
                    </RowCard>
                  ))
                ) : (
                  <Empty>No reports. Your community is healthy.</Empty>
                )}
              </Section>
            )}

            {tab === 'analytics' && (
              <Section>
                <Stats items={[['Members', members.length], ['Online now', members.filter((m) => m.online).length], ['Messages', messages.length], ['Channels', channels.length]]} />
                <Empty>Detailed dashboards, retention curves, channel heat maps, and revenue analytics are coming soon.</Empty>
              </Section>
            )}

            {tab === 'danger' && (
              <Section>
                <div className="rounded-xl border border-danger/30 bg-danger/[0.06] p-5">
                  <div className="text-base font-black text-danger">Delete community</div>
                  <p className="mt-1 text-sm leading-relaxed text-mid">
                    Permanent deletion needs server-side owner verification. This action is intentionally blocked in the client-only admin drawer.
                  </p>
                  <Button variant="danger" className="mt-4" onClick={() => toast('Deletion requires backend confirmation', 'info')}>
                    <Trash2 size={15} /> Request deletion
                  </Button>
                </div>
              </Section>
            )}
          </div>
        </main>
      </aside>
    </div>
  );
}

function useLocalSetting<T>(key: string, initial: T): [T, (value: T) => void] {
  const storageKey = `winr-admin:${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const save = (next: T) => {
    setValue(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };
  return [value, save];
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function accessValue(community: Community): string {
  if (community.privacy === 'private') return 'private';
  if (community.price_cents > 0 || community.access === 'paid') return 'paid';
  return 'free';
}

function labelFor(tab: AdminTab): string {
  return TABS.find((t) => t.id === tab)?.label ?? 'Settings';
}

function subFor(tab: AdminTab): string {
  return {
    general: 'Core details, access, and posting requirements',
    channels: 'Create, organize, and configure channels',
    roles: 'Role tiers and permission surfaces',
    members: 'Search, inspect, and moderate members',
    invites: 'Invite links and join approval',
    moderation: 'Reports, bans, and warning queues',
    analytics: 'Community health and activity',
    danger: 'Destructive owner actions',
  }[tab];
}

function Section({ children }: { children: ReactNode }) {
  return <div className="max-w-3xl">{children}</div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.5px] text-lo">{label}</label>
      {children}
    </div>
  );
}

function RowCard({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3 rounded-xl border border-edge bg-white/[0.025] px-3.5 py-3 transition-colors hover:bg-white/[0.04]">
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-mid">{icon}</span>
      {children}
    </div>
  );
}

function AvatarCell({ avatarUrl, name }: { avatarUrl?: string | null; name: string }) {
  return (
    <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 bg-cover bg-center text-xs font-black text-white" style={avatarBg(avatarUrl)}>
      {!avatarUrl && avatarInitial(name)}
    </span>
  );
}

function ToggleRow({ title, desc, checked, onToggle }: { title: string; desc: string; checked: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="mb-4 flex w-full items-center justify-between gap-4 rounded-xl border border-edge bg-white/[0.025] px-4 py-3 text-left" onClick={onToggle}>
      <span>
        <span className="block text-sm font-bold text-hi">{title}</span>
        <span className="mt-0.5 block text-xs text-lo">{desc}</span>
      </span>
      <span className={cn('relative h-6 w-11 rounded-pill border transition-colors', checked ? 'border-white/30 bg-white' : 'border-white/15 bg-white/[0.06]')}>
        <span className={cn('absolute top-1 h-4 w-4 rounded-full transition-transform', checked ? 'left-6 bg-black' : 'left-1 bg-white/50')} />
      </span>
    </button>
  );
}

function Stats({ items }: { items: Array<[string, number]> }) {
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-xl border border-edge bg-white/[0.03] p-4">
          <div className="text-2xl font-black text-hi">{value}</div>
          <div className="mt-0.5 text-xs text-lo">{label}</div>
        </div>
      ))}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-5 py-10 text-center text-sm text-lo">
      {children}
    </div>
  );
}
